import * as React from 'react';

import type { HiveWorkingBranch, HiveWorkingBranchState } from '@gitbook/hive-client';
import type { Revision } from '@gitbook/hive-core';
import { assertNever } from '@gitbook/util-ts';

import { useHiveComputedRevision } from './useHiveComputedRevision';

const HiveRevisionContext = React.createContext<HiveWorkingBranchState | null>(null);

interface UseHiveWorkingBranchOptions {
    /**
     * If true, the hook will subscribe to future updates in the branch.
     * @default true
     */
    subscribe?: boolean;
}

/**
 * Provide a revision to the react tree.
 * The revision can come from a branch or be a frozen one.
 */
export function HiveRevisionProvider(
    props: React.PropsWithChildren<
        | {
              revision: Revision;
              branch?: undefined;
          }
        | {
              revision?: undefined;
              branch: HiveWorkingBranch;
          }
    >
): React.ReactElement {
    const { revision, branch, children } = props;

    if (revision) {
        return <ProviderFrozenRevision revision={revision}>{children}</ProviderFrozenRevision>;
    }

    if (branch) {
        return <ProviderWorkingBranch branch={branch}>{children}</ProviderWorkingBranch>;
    }

    assertNever(props);
}

function ProviderFrozenRevision(
    props: React.PropsWithChildren<{ revision: Revision }>
): React.ReactElement {
    const { revision: sourceRevision, children } = props;
    const state = useHiveComputedRevision(sourceRevision);
    return <HiveRevisionContext.Provider value={state}>{children}</HiveRevisionContext.Provider>;
}

function ProviderWorkingBranch(
    props: React.PropsWithChildren<{ branch: HiveWorkingBranch }>
): React.ReactElement {
    const { branch, children } = props;

    const state = useHiveWorkingBranch(branch);
    return <HiveRevisionContext.Provider value={state}>{children}</HiveRevisionContext.Provider>;
}

/**
 * Get the current revision from the context.
 */
export function useHiveRevision(): HiveWorkingBranchState {
    const current = React.useContext(HiveRevisionContext);
    if (!current) {
        throw new Error('No revision provided');
    }
    return current;
}

/**
 * Get the current revision of a branch.
 * This hook suspends rendering while fetching the latest revision.
 */
export function useHiveWorkingBranch(
    branch: HiveWorkingBranch,
    options?: UseHiveWorkingBranchOptions
): HiveWorkingBranchState;
export function useHiveWorkingBranch(branch: null, options?: UseHiveWorkingBranchOptions): null;
export function useHiveWorkingBranch(
    branch: HiveWorkingBranch | null,
    options?: UseHiveWorkingBranchOptions
): HiveWorkingBranchState | null;
export function useHiveWorkingBranch(
    branch: HiveWorkingBranch | null,
    options: UseHiveWorkingBranchOptions = {}
): HiveWorkingBranchState | null {
    const { subscribe = true } = options;

    const subscription = React.useMemo(() => {
        return {
            getSnapshot: () => {
                if (!branch) {
                    return null;
                }

                return branch.state;
            },
            subscribe: (callback: () => void) => {
                if (!subscribe) {
                    return () => {};
                }

                if (!branch) {
                    return () => {};
                }

                return branch.listen('update', callback);
            },
        };
    }, [branch, subscribe]);

    return React.useSyncExternalStore(
        subscription.subscribe,
        subscription.getSnapshot,
        subscription.getSnapshot
    );
}
